import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { assessmentsApi, type AssessmentSubmission } from '@/lib/api/assessments';

interface Props {
  assessmentID: string;
  assessmentTitle: string;
  onClose: () => void;
}

export function AssessmentSubmissions({ assessmentID, assessmentTitle, onClose }: Props) {
  const [submissions, setSubmissions] = useState<AssessmentSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    assessmentsApi.listSubmissions(assessmentID)
      .then(setSubmissions)
      .catch(() => toast.error('Failed to load submissions'))
      .finally(() => setLoading(false));
  }, [assessmentID]);

  const avg = submissions.length
    ? Math.round(submissions.reduce((s, sub) => s + (sub.max_score > 0 ? sub.score / sub.max_score : 0), 0) / submissions.length * 100)
    : 0;

  return (
    <div className="mt-2 mb-4 ml-4 border-l-2 border-purple-500/30 pl-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-semibold">{assessmentTitle} — Submissions</span>
          {!loading && (
            <span className="ml-2 text-xs text-[#6b7280]">
              {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
              {submissions.length > 0 && ` · avg ${avg}%`}
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-xs text-[#6b7280] hover:text-[#111827] border border-[#ececec] rounded px-2 py-1">Close</button>
      </div>

      {loading && <p className="text-xs text-[#6b7280]">Loading…</p>}
      {!loading && submissions.length === 0 && <p className="text-xs text-[#6b7280]">No submissions yet.</p>}
      {!loading && submissions.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-[#ececec]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#ececec] bg-[#f3f4f6]/40">
                <th className="px-3 py-2 text-left text-xs font-medium text-[#6b7280]">Student</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-[#6b7280]">Score</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-[#6b7280]">%</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[#6b7280]">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(sub => {
                const pct = sub.max_score > 0 ? Math.round((sub.score / sub.max_score) * 100) : 0;
                return (
                  <tr key={sub.id} className="border-b border-[#ececec] last:border-0">
                    <td className="px-3 py-2.5 font-medium">{sub.username || sub.user_id.slice(0, 8)}</td>
                    <td className="px-3 py-2.5 text-center">{sub.score}/{sub.max_score}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${pct >= 70 ? 'bg-green-500/10 text-green-600 dark:text-green-400' : pct >= 50 ? 'bg-yellow-500/10 text-yellow-600' : 'bg-red-500/10 text-red-500'}`}>
                        {pct}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[#6b7280]">{new Date(sub.submitted_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
